(() => {
  const ROOT_SEL = "[data-south-mega-root]";
  const BP_MEGA = 760;
  const BP_SUPPORT = 989;

  const bindHoverDetails = ({ elements, panelSelector, breakpoint, guardKey }) => {
    if (!elements || !elements.length) return;

    const OPEN_DELAY = 60;
    const CLOSE_DELAY = 160;

    const closeOthers = (keep) => {
      elements.forEach((el) => {
        if (el !== keep) el.open = false;
      });
    };

    elements.forEach((el) => {
      if (el.dataset[guardKey]) return;
      el.dataset[guardKey] = "1";

      const panel = el.querySelector(panelSelector);

      const canOpen = () => {
        if (!breakpoint) return true;
        return window.innerWidth > breakpoint;
      };

      const openNow = () => {
        if (!canOpen()) return;
        closeOthers(el);
        el.open = true;
      };

      const scheduleOpen = () => {
        if (!canOpen()) return;
        clearTimeout(el._closeT);
        clearTimeout(el._openT);
        el._openT = setTimeout(openNow, OPEN_DELAY);
      };

      const scheduleClose = () => {
        clearTimeout(el._openT);
        clearTimeout(el._closeT);
        el._closeT = setTimeout(() => {
          el.open = false;
        }, CLOSE_DELAY);
      };

      el.addEventListener("mouseenter", scheduleOpen);
      el.addEventListener("mouseleave", scheduleClose);

      if (panel) {
        panel.addEventListener("mouseenter", () => clearTimeout(el._closeT));
        panel.addEventListener("mouseleave", scheduleClose);
      }

      el.addEventListener("focusin", openNow);

      el.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
          el.open = false;
          const summary = el.querySelector("summary");
          if (summary) summary.focus();
        }
      });

      el.addEventListener("toggle", () => {
        if (el.open) closeOthers(el);
      });
    });
  };

  const initMega = (scope = document) => {
    scope.querySelectorAll(ROOT_SEL).forEach((root) => {
      if (root.dataset.southInit) return;
      root.dataset.southInit = "1";

      const header = root.closest("[data-south-header]") || document.querySelector("[data-south-header]") || root;
      const productsRow = header.querySelector("[data-south-products-row]");
      const triggers = root.querySelectorAll("[data-south-mega-trigger]");
      const map = new Map();

      let openTimer = null;
      let closeTimer = null;
      const OPEN_DELAY = 60;
      const CLOSE_DELAY = 160;

      const getAnchorRect = () => (
        productsRow ? productsRow.getBoundingClientRect() : header.getBoundingClientRect()
      );

      const getActivePanel = (dd) =>
        dd.querySelector(".southMegaPanel__panel.is-active") ||
        dd.querySelector(".southMegaPanel__panel:not([hidden])");

      const getVisibleTileCards = (dd) => {
        const style = dd.dataset.panelStyle || "grid";

        if (style === "rows") return [];

        if (style === "stacked_rows") {
          return [...dd.querySelectorAll(".southMegaPanel__grid--stacked > .southMegaCard")];
        }

        const panel = getActivePanel(dd);
        if (!panel) return [];

        return [...panel.querySelectorAll(".southMegaPanel__grid > .southMegaCard")];
      };

      const syncMultirowMode = (dd) => {
        const cards = getVisibleTileCards(dd);

        if (!cards.length) {
          dd.classList.remove("is-multirow");
          return false;
        }

        const rows = [];
        cards.forEach((card) => {
          const top = Math.round(card.getBoundingClientRect().top);
          const exists = rows.some((value) => Math.abs(value - top) <= 4);
          if (!exists) rows.push(top);
        });

        const isMultirow = rows.length > 1;
        dd.classList.toggle("is-multirow", isMultirow);
        return isMultirow;
      };

      const calcPanelBox = (dd) => {
        const vw = window.innerWidth;
        const width = Math.min(vw - 12, 1680);
        const left = Math.round((vw - width) / 2);
        const isMultirow = syncMultirowMode(dd);
        return { left, width, isMultirow };
      };

      const hydrateImages = (dd, btn) => {
        dd.querySelectorAll("img.southMegaCard__img").forEach((img) => {
          const mark = () => {
            img.classList.add("is-loaded");
            requestAnimationFrame(() => {
              if (dd.classList.contains("is-open") && btn) {
                positionDropdown(dd, btn);
              }
            });
          };

          if (img.complete) mark();
          else img.addEventListener("load", mark, { once: true });
        });
      };

      const positionDropdown = (dd, btn) => {
        const anchorRect = getAnchorRect();
        const box = calcPanelBox(dd);
        const top = Math.round(anchorRect.bottom + 10);
        const style = dd.dataset.panelStyle || "grid";
        const availableHeight = Math.max(300, window.innerHeight - top - 24);

        let maxHeight = Math.min(680, availableHeight);

        if (style === "rows") {
          maxHeight = Math.min(620, availableHeight);
        } else if (style === "stacked_rows") {
          maxHeight = box.isMultirow ? Math.min(560, availableHeight) : Math.min(640, availableHeight);
        } else if (style === "grid") {
          maxHeight = box.isMultirow ? Math.min(560, availableHeight) : Math.min(680, availableHeight);
        }

        dd.style.top = `${top}px`;
        dd.style.left = `${box.left}px`;
        dd.style.width = `${box.width}px`;
        dd.style.maxHeight = `${maxHeight}px`;
        dd.style.overflowY = "auto";
        dd.style.overflowX = "hidden";

        const btnRect = btn.getBoundingClientRect();
        const notchX = Math.round((btnRect.left + btnRect.width / 2) - box.left);
        dd.style.setProperty("--south-notch-x", `${notchX}px`);
      };

      const closeAll = () => {
        map.forEach((dd, btn) => {
          dd.classList.remove("is-open");
          dd.classList.remove("is-multirow");
          btn.setAttribute("aria-expanded", "false");
        });
      };

      const open = (btn) => {
        if (window.innerWidth <= BP_MEGA) return;

        closeAll();

        const dd = map.get(btn);
        if (!dd) return;

        dd.classList.add("is-open");
        btn.setAttribute("aria-expanded", "true");

        positionDropdown(dd, btn);
        hydrateImages(dd, btn);

        requestAnimationFrame(() => {
          if (dd.classList.contains("is-open")) {
            positionDropdown(dd, btn);
          }
        });
      };

      const switchPanel = (dd, target) => {
        const cats = dd.querySelectorAll("[data-south-mega-cat]");
        const next = dd.querySelector(`[data-south-mega-panel="${target}"]`);
        if (!next) return;

        cats.forEach((c) => c.classList.toggle("is-active", c.dataset.target === target));

        const panels = dd.querySelectorAll(".southMegaPanel__panel");
        panels.forEach((p) => {
          p.hidden = true;
          p.classList.remove("is-active");
        });

        next.hidden = false;
        next.classList.add("is-active");

        const trigger = [...map.entries()].find(([, panel]) => panel === dd)?.[0];
        hydrateImages(dd, trigger);

        requestAnimationFrame(() => {
          if (dd.classList.contains("is-open") && trigger) {
            positionDropdown(dd, trigger);
          }
        });
      };

      triggers.forEach((btn) => {
        const id = btn.getAttribute("aria-controls");
        const dd = document.getElementById(id);
        if (!dd) return;

        document.body.appendChild(dd);
        map.set(btn, dd);

        const cats = dd.querySelectorAll("[data-south-mega-cat]");
        const closeBtn = dd.querySelector("[data-south-mega-close]");

        cats.forEach((el) => {
          el.addEventListener("mouseenter", () => switchPanel(dd, el.dataset.target));
          el.addEventListener("focus", () => switchPanel(dd, el.dataset.target));
          el.addEventListener("click", () => switchPanel(dd, el.dataset.target));
        });

        if (closeBtn) {
          closeBtn.addEventListener("click", closeAll);
        }

        dd.addEventListener("mouseenter", () => clearTimeout(closeTimer));

        dd.addEventListener("mouseleave", () => {
          clearTimeout(openTimer);
          closeTimer = setTimeout(closeAll, CLOSE_DELAY);
        });

        dd.addEventListener("keydown", (e) => {
          if (e.key === "Escape") {
            closeAll();
            btn.focus();
          }
        });
      });

      triggers.forEach((btn) => {
        btn.addEventListener("mouseenter", () => {
          clearTimeout(closeTimer);
          openTimer = setTimeout(() => open(btn), OPEN_DELAY);
        });

        btn.addEventListener("mouseleave", () => {
          clearTimeout(openTimer);
          closeTimer = setTimeout(closeAll, CLOSE_DELAY);
        });

        btn.addEventListener("focusin", () => {
          clearTimeout(closeTimer);
          open(btn);
        });

        btn.addEventListener("click", (e) => {
          e.preventDefault();
          const dd = map.get(btn);
          if (!dd) return;
          if (dd.classList.contains("is-open")) closeAll();
          else open(btn);
        });
      });

      document.addEventListener("click", (e) => {
        const anyOpen = [...map.values()].some((dd) => dd.classList.contains("is-open"));
        if (!anyOpen) return;

        for (const [btn, dd] of map.entries()) {
          if (btn.contains(e.target) || dd.contains(e.target)) return;
        }
        closeAll();
      });

      const rep = () => {
        map.forEach((dd, btn) => {
          if (dd.classList.contains("is-open")) {
            positionDropdown(dd, btn);
          }
        });
      };

      window.addEventListener("resize", rep, { passive: true });
      window.addEventListener("scroll", rep, { passive: true });
    });
  };

  const initSupportStripHover = (scope = document) => {
    const header = scope.querySelector("[data-south-header]") || document.querySelector("[data-south-header]");
    if (!header) return;

    const dds = header.querySelectorAll("[data-south-inline-dd]");
    bindHoverDetails({
      elements: [...dds],
      panelSelector: ".southSupportInline__panel",
      breakpoint: BP_SUPPORT,
      guardKey: "inlineHoverInit"
    });
  };

  const initMobileDrawer = (scope = document) => {
    const header = scope.querySelector("[data-south-header]") || document.querySelector("[data-south-header]");
    if (!header) return;

    const toggles = header.querySelectorAll("[data-south-mobile-toggle]");

    toggles.forEach((toggle) => {
      if (toggle.dataset.southMobileInit) return;
      toggle.dataset.southMobileInit = "1";

      const setDrawerTop = () => {
        const rect = header.getBoundingClientRect();
        toggle.style.setProperty("--south-mobile-drawer-top", `${Math.round(rect.bottom)}px`);
      };

      const closeDrawer = () => {
        toggle.open = false;
        document.documentElement.classList.remove("south-mobile-open");
      };

      const syncState = () => {
        if (window.innerWidth > BP_MEGA) {
          closeDrawer();
          return;
        }

        setDrawerTop();

        if (toggle.open) document.documentElement.classList.add("south-mobile-open");
        else document.documentElement.classList.remove("south-mobile-open");
      };

      toggle.addEventListener("toggle", syncState);

      toggle.addEventListener("keydown", (e) => {
        if (e.key === "Escape") closeDrawer();
      });

      document.addEventListener("click", (e) => {
        if (!toggle.open) return;
        if (toggle.contains(e.target)) return;
        closeDrawer();
      });

      window.addEventListener("resize", syncState, { passive: true });
      window.addEventListener("scroll", () => {
        if (!toggle.open) return;
        setDrawerTop();
      }, { passive: true });
    });
  };

  const init = (scope = document) => {
    initMega(scope);
    initSupportStripHover(scope);
    initMobileDrawer(scope);
  };

  document.addEventListener("DOMContentLoaded", () => init(document));
  document.addEventListener("shopify:section:load", (e) => init(e.target));
})();